import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import LoadingButton from '@mui/lab/LoadingButton';
import { BRAND_ORANGE } from '../../../../styles/portalTheme';
import {
  Alert,
  Box,
  Button,
  Dialog,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  buildAttendanceFinalizeDefaults,
  buildFinalizeAttendancePayload,
  computeAttendancePreview,
  formatAttendancePreviewLines,
  isAttendanceFinalized,
  validateAttendanceFinalizeForm,
} from '../attendanceFinalizeUtils';

const ACTION_CORAL = '#e0613f';
const INFO_BLUE = '#2b7de9';
const INFO_BG = '#f0f7ff';
const INFO_BORDER = '#d6e8f7';

const attendanceFieldSx = {
  '& .MuiInputLabel-root': {
    fontWeight: 600,
    color: 'text.secondary',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: 'text.primary',
  },
};

const LockedField = ({ label, value }) => (
  <TextField
    fullWidth
    label={label}
    value={value}
    InputProps={{
      readOnly: true,
      endAdornment: (
        <InputAdornment position="end">
          <LockOutlinedIcon sx={{ fontSize: 18, color: '#98a2b3' }} />
        </InputAdornment>
      ),
    }}
    sx={{
      ...attendanceFieldSx,
      '& .MuiOutlinedInput-root': {
        bgcolor: 'background.default',
        '& fieldset': { borderColor: '#e4e7ec' },
      },
    }}
  />
);

const VerifyAttendanceModal = ({ open, onClose, event, submitting = false, onFinalize }) => {
  const defaults = useMemo(() => buildAttendanceFinalizeDefaults(event), [event]);
  const readOnly = useMemo(() => isAttendanceFinalized(event), [event]);
  const [form, setForm] = useState(defaults);

  useEffect(() => {
    if (open) setForm(defaults);
  }, [open, defaults]);

  const preview = useMemo(
    () =>
      formatAttendancePreviewLines(
        computeAttendancePreview({
          totalRsvps: defaults.totalRsvps,
          paidTickets: defaults.paidTickets,
          actualAttendance: form.actualAttendance,
          walkIns: form.walkIns,
          noShows: form.noShows,
        }),
      ),
    [defaults.paidTickets, defaults.totalRsvps, form.actualAttendance, form.walkIns, form.noShows],
  );

  const updateField = (key) => (e) => {
    const raw = e.target.value;
    if (raw === '') {
      setForm((prev) => ({ ...prev, [key]: '' }));
      return;
    }
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return;
    setForm((prev) => ({ ...prev, [key]: parsed }));
  };

  const handleFinalize = () => {
    const validationError = validateAttendanceFinalizeForm({
      ...form,
      paidTickets: defaults.paidTickets,
    });
    if (validationError) {
      toast.error(validationError);
      return;
    }
    onFinalize(buildFinalizeAttendancePayload(form));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 2.5, mx: { xs: 1.5, sm: 2 } } }}
    >
      <Box sx={{ position: 'relative', px: { xs: 2, sm: 3 }, pt: 2.5, pb: 0 }}>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          disabled={submitting}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
        <Typography
          align="center"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.2rem', sm: '1.35rem' },
            color: 'text.primary',
            pr: 4,
          }}
        >
          Verify Attendance Data
        </Typography>
        <Typography
          align="center"
          sx={{ color: 'text.secondary', fontSize: '0.9rem', mt: 0.5, mb: 2 }}
        >
          {readOnly ? 'Locked attendance record' : 'Finalize numbers for the event'}
        </Typography>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {readOnly ? (
          <Alert
            icon={<LockOutlinedIcon sx={{ color: '#344054' }} />}
            severity="info"
            sx={{
              alignItems: 'flex-start',
              bgcolor: 'action.hover',
              color: '#344054',
              border: '1px solid #e4e7ec',
              borderRadius: 2,
            }}
          >
            Attendance data is finalized and locked. Contact support if a correction is required.
          </Alert>
        ) : null}
        {!readOnly ? (
          <Alert
            icon={<InfoOutlinedIcon sx={{ color: '#c56f4f' }} />}
            severity="warning"
            sx={{
              alignItems: 'flex-start',
              bgcolor: '#fff8f4',
              color: '#7a2e0c',
              border: '1px solid #f0c7ad',
              borderRadius: 2,
              '& .MuiAlert-message': { width: '100%' },
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 0.5 }}>
              Action Required: Locked Dataset
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', lineHeight: 1.45 }}>
              Once finalized, this attendance data will be locked and synced to your payout ledger.
              Please ensure all walk-ins and no-shows are accurately recorded.
            </Typography>
          </Alert>
        ) : null}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <LockedField label="Total RSVPs" value={defaults.totalRsvps} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <LockedField label="Paid Tickets" value={defaults.paidTickets} />
          </Grid>
          <Grid item xs={12} sm={6}>
            {readOnly ? (
              <LockedField label="Actual Attendance" value={form.actualAttendance} />
            ) : (
              <TextField
                fullWidth
                type="number"
                label="Actual Attendance"
                value={form.actualAttendance}
                onChange={updateField('actualAttendance')}
                inputProps={{ min: 0 }}
                disabled={submitting}
                sx={attendanceFieldSx}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            {readOnly ? (
              <LockedField label="Walk-Ins" value={form.walkIns} />
            ) : (
              <TextField
                fullWidth
                type="number"
                label="Walk-Ins"
                value={form.walkIns}
                onChange={updateField('walkIns')}
                inputProps={{ min: 0 }}
                disabled={submitting}
                sx={attendanceFieldSx}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            {readOnly ? (
              <LockedField label="No-Shows" value={form.noShows} />
            ) : (
              <TextField
                fullWidth
                type="number"
                label="No-Shows"
                value={form.noShows}
                onChange={updateField('noShows')}
                inputProps={{ min: 0 }}
                disabled={submitting}
                sx={attendanceFieldSx}
              />
            )}
          </Grid>
        </Grid>

        <Box
          sx={{
            border: `1px solid ${INFO_BORDER}`,
            borderRadius: 2,
            bgcolor: INFO_BG,
            p: 1.75,
          }}
        >
          <Typography sx={{ fontWeight: 700, color: INFO_BLUE, fontSize: '0.95rem', mb: 1.25 }}>
            Attendance Calculation Preview
          </Typography>
          {[
            { label: 'Paid attendees who showed up:', value: preview.paidAttendeesLine },
            { label: 'No-show rate:', value: preview.noShowRateLine },
            { label: 'Overall attendance rate:', value: preview.overallAttendanceLine },
          ].map((row) => (
            <Box
              key={row.label}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 1.5,
                flexWrap: 'wrap',
                py: 0.35,
                color: INFO_BLUE,
                fontSize: '0.875rem',
              }}
            >
              <Typography component="span" sx={{ fontSize: 'inherit', color: 'inherit' }}>
                {row.label}
              </Typography>
              <Typography
                component="span"
                sx={{ fontWeight: 700, fontSize: 'inherit', color: 'inherit' }}
              >
                {row.value}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box>
          <Typography
            sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.primary', mb: 0.75 }}
          >
            Operational Notes
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            placeholder="Add any operational notes about check-in, capacity, or on-site issues…"
            value={form.operationalNotes}
            onChange={(e) => setForm((prev) => ({ ...prev, operationalNotes: e.target.value }))}
            disabled={submitting || readOnly}
            InputProps={readOnly ? { readOnly: true } : undefined}
            helperText={
              readOnly ? undefined : 'These notes will be included in the final event report'
            }
            FormHelperTextProps={{ sx: { color: 'text.secondary', mt: 0.75 } }}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 1.25,
            alignItems: 'flex-start',
            bgcolor: '#f2f4f7',
            borderRadius: 2,
            p: 1.5,
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 18, color: '#344054', mt: 0.2 }} />
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#101828', mb: 0.35 }}>
              Dataset Finalization
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.45, color: '#475467' }}>
              After finalization, this attendance data will be locked and used for analytics,
              partner payouts, and future event planning.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            justifyContent: readOnly ? 'flex-end' : 'space-between',
            gap: 1.25,
            pt: 0.5,
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={submitting}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 1.5,
              color: BRAND_ORANGE,
              borderColor: '#e6b48f',
              px: 2.5,
              width: { xs: '100%', sm: 'auto' },
              '&:hover': { borderColor: BRAND_ORANGE, bgcolor: 'rgba(224, 104, 10, 0.04)' },
            }}
          >
            {readOnly ? 'Close' : 'Cancel'}
          </Button>
          {!readOnly ? (
            <LoadingButton
              fullWidth
              variant="contained"
              loading={submitting}
              onClick={handleFinalize}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 1.5,
                minHeight: 44,
                px: 2.5,
                width: { xs: '100%', sm: 'auto' },
                bgcolor: ACTION_CORAL,
                boxShadow: 'none',
                '&:hover': { bgcolor: 'error.dark', boxShadow: 'none' },
              }}
            >
              Finalize Attendance
            </LoadingButton>
          ) : null}
        </Box>
      </Box>
    </Dialog>
  );
};

export default VerifyAttendanceModal;
